// FloorPlanCanvas.tsx — 2D SVG floor plan renderer
// Architectural floor plan conventions (AIA/NCS):
//   - Wall bands with hatched poché fill, bold outer / thin inner lines
//   - Panel joints as dashed lines through wall section
//   - Panel marks as lollipop callouts (circle + leader stem) on EXTERIOR
//   - Staggered leader lengths (2 tiers) to prevent overlap on dense walls
//   - Dimension strings OUTSIDE building with extension lines + 45° tick marks
//   - Elevation labels INSIDE the building (interior face)
//   - Title block below the drawing

import { useMemo, useState, useRef, useEffect } from 'react';
import type { Elevation, PanelData } from '../types/panelvar';
import { computeFootprint, DIR_ANGLE } from '../utils/buildingFootprint';
import type { WallSegment2D } from '../utils/buildingFootprint';

interface Props {
  elevations: Elevation[];
  panels: PanelData[];
  jointWidth?: number;
  sheetName?: string;
}

/** Format inches as ft'-in" */
function fmtDim(inches: number): string {
  const ft = Math.floor(Math.abs(inches) / 12);
  const inc = Math.round(Math.abs(inches) % 12);
  const sign = inches < 0 ? '-' : '';
  if (inc === 12) return `${sign}${ft + 1}'-0"`;
  return `${sign}${ft}'-${inc}"`;
}

// ── Layout constants ───────────────────────────────────────────────────────
// Exterior annotation zone layers (distance from outer wall face, outward):
//   Layer 1: Extension lines start at wall face
//   Layer 2: Dimension string (overall wall length)
//   Layer 3: Lollipop callouts tier 1 (even-indexed panels)
//   Layer 4: Lollipop callouts tier 2 (odd-indexed panels)
// All distances are multiplied by `scale` in the component.

const DIM_LINE_OFFSET = 120;   // dimension line distance from wall face
const LOLLIPOP_TIER1 = 160;    // short stem (even panels)
const LOLLIPOP_TIER2 = 200;    // long stem (odd panels)
const LOLLIPOP_RADIUS_FACTOR = 12; // callout sizing factor = scale * this

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 20;
const CALLOUT_ZOOM_THRESHOLD = 2.5;
const zoomBtnStyle: React.CSSProperties = {
  width: 28, height: 28, border: '1px solid #bbb', borderRadius: 4,
  background: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: '26px',
  textAlign: 'center', userSelect: 'none', color: '#333', fontWeight: 'bold',
};

export default function FloorPlanCanvas({ elevations, panels, jointWidth = 0.75, sheetName }: Props) {
  const footprint = useMemo(
    () => computeFootprint(elevations, panels, jointWidth),
    [elevations, panels, jointWidth],
  );
  // Group consecutive walls by direction for merged elevation labels
  const mergedLabels = useMemo(() => {
    const groups: { name: string; direction: string; walls: WallSegment2D[] }[] = [];
    for (const w of footprint.walls) {
      const last = groups[groups.length - 1];
      if (last && last.direction === w.direction) {
        last.walls.push(w);
      } else {
        const baseName = w.name.includes(' - ') ? w.name.split(' - ')[0].trim() : w.name;
        groups.push({ name: baseName, direction: w.direction, walls: [w] });
      }
    }
    return groups;
  }, [footprint.walls]);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragOrigin, setDragOrigin] = useState<{ sx: number; sy: number; px: number; py: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Native wheel listener (passive: false) for zoom
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom(z => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z * factor)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  if (footprint.walls.length === 0) {
    return (
      <div className="building-canvas-empty">
        <p>No elevations with panels to draw.</p>
        <p className="building-canvas-hint">Assign panels to elevations to generate a floor plan.</p>
      </div>
    );
  }

  // Average thickness across all panels for wall band width
  const allPanels = footprint.walls.flatMap(w => w.panels);
  const avgThk = allPanels.length > 0
    ? allPanels.reduce((s, p) => s + p.mpvar.thickness, 0) / allPanels.length
    : 8;

  // Compute bounding box of all wall segments (including inner face)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const w of footprint.walls) {
    const perpX = Math.cos(w.angle + Math.PI / 2);
    const perpY = -Math.sin(w.angle + Math.PI / 2);
    for (const pt of [
      { x: w.startX, y: w.startY },
      { x: w.endX, y: w.endY },
      { x: w.startX + perpX * avgThk, y: w.startY + perpY * avgThk },
      { x: w.endX + perpX * avgThk, y: w.endY + perpY * avgThk },
    ]) {
      minX = Math.min(minX, pt.x);
      minY = Math.min(minY, pt.y);
      maxX = Math.max(maxX, pt.x);
      maxY = Math.max(maxY, pt.y);
    }
  }

  // Compute scale from building dimensions alone (not viewBox) to avoid circular dependency
  const bldgW = maxX - minX;
  const bldgH = maxY - minY;
  const rawScale = Math.max(bldgW, bldgH) / 800;
  const fontSize = rawScale * 14;
  const smallFont = rawScale * 10;

  // Annotation zone offsets — derived from scale so they clear text at any building size
  const dimLineOff = DIM_LINE_OFFSET;   // dimension line from wall face
  const dimTextOff = dimLineOff + smallFont * 2;  // dim text clears the dim line
  const elevLabelOff = dimTextOff + fontSize * 2.5; // elev label clears dim text entirely

  // Padding must accommodate outermost layer (elev label + its own text height + grid bubbles)
  const pad = elevLabelOff + fontSize * 2;
  const vbX = minX - pad;
  const vbY = minY - pad;
  const vbW = maxX - minX + pad * 2;
  const vbH = maxY - minY + pad * 2;

  // Final scale from full viewBox (for strokes — fonts use rawScale-derived values)
  const scale = Math.max(vbW, vbH) / 800;
  const thin = scale * 0.5;
  const med = scale * 1;
  const lollipopR = scale * LOLLIPOP_RADIUS_FACTOR;

  // Dynamic viewBox based on zoom/pan
  const effW = vbW / zoom;
  const effH = vbH / zoom;
  const effX = vbX + (vbW - effW) / 2 + pan.x;
  const effY = vbY + (vbH - effH) / 2 + pan.y;
  const showCallouts = zoom >= CALLOUT_ZOOM_THRESHOLD;

  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    (e.target as Element).setPointerCapture(e.pointerId);
    setDragOrigin({ sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y });
  };
  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragOrigin || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    setPan({
      x: dragOrigin.px - (e.clientX - dragOrigin.sx) * (effW / r.width),
      y: dragOrigin.py - (e.clientY - dragOrigin.sy) * (effH / r.height),
    });
  };
  const handlePointerUp = () => setDragOrigin(null);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Zoom controls */}
      <div style={{
        position: 'absolute', top: 8, right: 8, display: 'flex',
        flexDirection: 'column', gap: 4, zIndex: 10,
      }}>
        <button type="button" onClick={() => setZoom(z => Math.min(MAX_ZOOM, z * 1.4))} style={zoomBtnStyle}>+</button>
        <button
          type="button"
          onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
          style={{ ...zoomBtnStyle, fontSize: 10, fontWeight: 'normal' }}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button type="button" onClick={() => setZoom(z => Math.max(MIN_ZOOM, z / 1.4))} style={zoomBtnStyle}>−</button>
      </div>
      <svg
        ref={svgRef}
        className="building-canvas-svg"
        viewBox={`${effX} ${effY} ${effW} ${effH}`}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{ cursor: dragOrigin ? 'grabbing' : 'grab', touchAction: 'none' }}
      >
      <defs>
        <pattern
          id="floor-wall-hatch"
          width={scale * 6}
          height={scale * 6}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1={0} y1={0} x2={0} y2={scale * 6} stroke="#bbb" strokeWidth={thin * 0.6} />
        </pattern>
        {/* Construction grid — matches 3D view: 120" cells, 600" sections */}
        <pattern
          id="floor-grid-cell"
          width={120}
          height={120}
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 120 0 L 0 0 0 120"
            fill="none"
            stroke="#c8d8e8"
            strokeWidth={thin * 0.5}
          />
        </pattern>
        <pattern
          id="floor-grid-section"
          width={600}
          height={600}
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 600 0 L 0 0 0 600"
            fill="none"
            stroke="#5080b0"
            strokeWidth={thin * 1.2}
          />
        </pattern>
      </defs>

      {/* Grid background — minor + major lines */}
      <rect x={vbX} y={vbY} width={vbW} height={vbH} fill="#f8fafc" />
      <rect x={vbX} y={vbY} width={vbW} height={vbH} fill="url(#floor-grid-cell)" />
      <rect x={vbX} y={vbY} width={vbW} height={vbH} fill="url(#floor-grid-section)" />

      {/* ── Grid labels (numbers along X, letters along Y) ────────── */}
      <GridAxisLabels
        vbX={vbX} vbY={vbY} vbW={vbW} vbH={vbH}
        minX={minX} minY={minY}
        scale={scale} thin={thin}
      />

      {/* ── Wall bands (poché hatch, bold exterior / thin interior) ──── */}
      {footprint.walls.map((w) => (
        <WallBand key={w.elevId} wall={w} thickness={avgThk} thin={thin} med={med} />
      ))}

      {/* ── Panel joint lines through wall section ───────────────────── */}
      {footprint.walls.map((w) => (
        <PanelJoints
          key={`joints-${w.elevId}`}
          wall={w}
          thickness={avgThk}
          jointWidth={jointWidth}
          thin={thin}
        />
      ))}

      {/* ── Elevation labels (OUTSIDE, beyond dimension lines) ───── */}
      {/* Group sub-elevations by direction so only one label per main elevation */}
      {mergedLabels.map((g) => (
        <MergedElevationLabel
          key={`label-${g.direction}-${g.walls[0].elevId}`}
          walls={g.walls}
          name={g.name}
          offset={elevLabelOff}
          fontSize={fontSize}
        />
      ))}

      {/* ── Dimension strings (OUTSIDE, with extension lines + ticks) ── */}
      {footprint.walls.map((w) => (
        <DimensionString
          key={`dim-${w.elevId}`}
          wall={w}
          offset={dimLineOff}
          fontSize={smallFont}
          thin={thin}
          scale={scale}
        />
      ))}

      {/* ── Panel callouts (OUTSIDE, staggered) — visible on zoom ──── */}
      {showCallouts && footprint.walls.map((w) => (
        <PanelLollipops
          key={`lol-${w.elevId}`}
          wall={w}
          jointWidth={jointWidth}
          tier1={LOLLIPOP_TIER1}
          tier2={LOLLIPOP_TIER2}
          radius={lollipopR}
          thin={thin}
          fontSize={smallFont}
        />
      ))}

      {/* ── Zoom hint when callouts hidden (inside building) ──────── */}
      {!showCallouts && (
        <text
          x={(minX + maxX) / 2}
          y={(minY + maxY) / 2 - fontSize * 0.6}
          textAnchor="middle"
          fontSize={smallFont * 0.85}
          fill="#999"
          fontStyle="italic"
        >
          Scroll or press + to zoom in and see panel marks
        </text>
      )}

      {/* ── Panel/elevation count (inside building, below center) ──── */}
      <text
        x={(minX + maxX) / 2}
        y={(minY + maxY) / 2 + fontSize * 0.6}
        textAnchor="middle"
        fontSize={smallFont * 0.8}
        fill="#aaa"
      >
        {footprint.walls.length} elevation{footprint.walls.length !== 1 ? 's' : ''} · {allPanels.length} panel{allPanels.length !== 1 ? 's' : ''} · {footprint.isClosed ? 'Closed perimeter' : 'Open chain'}
      </text>

      {/* ── Title block ──────────────────────────────────────────────── */}
      <text
        x={(minX + maxX) / 2}
        y={maxY + pad - fontSize * 2.5}
        textAnchor="middle"
        fontSize={fontSize * 1.6}
        fontWeight="bold"
        fill="#1a2332"
      >
        {sheetName ?? 'Floor Plan'}
      </text>
    </svg>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

/** Outward perpendicular unit vector (exterior direction, away from building interior) */
function outward(angle: number): { ox: number; oy: number } {
  // In the SVG coordinate system (y-down), the chain walks clockwise:
  //   B(0) → R(3π/2) → F(π) → L(π/2)
  // The perpendicular angle+π/2 points outward from the building.
  return {
    ox: Math.cos(angle + Math.PI / 2),
    oy: -Math.sin(angle + Math.PI / 2),
  };
}

/** Wall-parallel unit vector */
function along(angle: number): { dx: number; dy: number } {
  return { dx: Math.cos(angle), dy: -Math.sin(angle) };
}

/**
 * Normalize a rotation angle (degrees) so text always reads right-side-up.
 * Text should read left→right (horizontal walls) or bottom→top (vertical walls).
 * If the raw angle would produce upside-down text (>90° or <-90°), flip 180°.
 */
function readableDeg(rawDeg: number): number {
  let d = rawDeg % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  if (d > 90) d -= 180;
  if (d < -90) d += 180;
  return d;
}

// ── Wall band ──────────────────────────────────────────────────────────────

function WallBand({ wall, thickness, thin, med }: {
  wall: WallSegment2D;
  thickness: number;
  thin: number;
  med: number;
}) {
  const { startX, startY, endX, endY, angle } = wall;
  const perpX = Math.cos(angle + Math.PI / 2);
  const perpY = -Math.sin(angle + Math.PI / 2);

  const polyPoints = [
    `${startX},${startY}`,
    `${endX},${endY}`,
    `${endX + perpX * thickness},${endY + perpY * thickness}`,
    `${startX + perpX * thickness},${startY + perpY * thickness}`,
  ].join(' ');

  return (
    <g>
      <polygon
        points={polyPoints}
        fill="url(#floor-wall-hatch)"
        stroke="#333"
        strokeWidth={med}
        strokeLinejoin="miter"
      />
      {/* Exterior face — bold (AIA convention: cut lines are heavy) */}
      <line x1={startX} y1={startY} x2={endX} y2={endY} stroke="#222" strokeWidth={med * 2} />
      {/* Interior face — thinner */}
      <line
        x1={startX + perpX * thickness}
        y1={startY + perpY * thickness}
        x2={endX + perpX * thickness}
        y2={endY + perpY * thickness}
        stroke="#444"
        strokeWidth={thin}
      />
    </g>
  );
}

// ── Panel joints ───────────────────────────────────────────────────────────

function PanelJoints({ wall, thickness, jointWidth: jw, thin }: {
  wall: WallSegment2D;
  thickness: number;
  jointWidth: number;
  thin: number;
}) {
  const { startX, startY, angle, panels, leftEndJoint, rightEndJoint } = wall;
  if (panels.length === 0) return null;

  const { dx, dy } = along(angle);

  const rects: JSX.Element[] = [];
  const minW = Math.max(thin * 1.5, jw > 0 ? jw : thin);

  // Left corner foam
  if (leftEndJoint > 0) {
    const cx = startX + dx * (leftEndJoint / 2);
    const cy = startY + dy * (leftEndJoint / 2);
    rects.push(
      <rect key="lf"
        x={cx - leftEndJoint / 2} y={cy - thickness / 2}
        width={Math.max(leftEndJoint, minW)} height={thickness}
        fill="white" stroke="#ccc" strokeWidth={thin * 0.3}
        transform={`rotate(${(-angle * 180) / Math.PI}, ${cx}, ${cy})`}
      />,
    );
  }

  // Inter-panel foam strips
  let offset = leftEndJoint;
  for (let i = 0; i < panels.length - 1; i++) {
    offset += panels[i].mpvar.width;
    const cx = startX + dx * (offset + jw / 2);
    const cy = startY + dy * (offset + jw / 2);
    rects.push(
      <rect key={i}
        x={cx - jw / 2} y={cy - thickness / 2}
        width={Math.max(jw, minW)} height={thickness}
        fill="white" stroke="#ccc" strokeWidth={thin * 0.3}
        transform={`rotate(${(-angle * 180) / Math.PI}, ${cx}, ${cy})`}
      />,
    );
    offset += jw;
  }

  // Right corner foam
  if (rightEndJoint > 0) {
    const totalConc = leftEndJoint + panels.reduce((s, p, i) => s + p.mpvar.width + (i < panels.length - 1 ? jw : 0), 0);
    const cx = startX + dx * (totalConc + rightEndJoint / 2);
    const cy = startY + dy * (totalConc + rightEndJoint / 2);
    rects.push(
      <rect key="rf"
        x={cx - rightEndJoint / 2} y={cy - thickness / 2}
        width={Math.max(rightEndJoint, minW)} height={thickness}
        fill="white" stroke="#ccc" strokeWidth={thin * 0.3}
        transform={`rotate(${(-angle * 180) / Math.PI}, ${cx}, ${cy})`}
      />,
    );
  }

  return <g>{rects}</g>;
}

// ── Merged elevation label (OUTSIDE, one per main direction) ──────────

function MergedElevationLabel({ walls, name, offset, fontSize }: {
  walls: WallSegment2D[];
  name: string;
  offset: number;
  fontSize: number;
}) {
  // Centroid of all wall start/end points in the group
  const pts = walls.flatMap(w => [
    { x: w.startX, y: w.startY },
    { x: w.endX, y: w.endY },
  ]);
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;

  // Use canonical direction angle for consistent outward placement
  const canonAngle = DIR_ANGLE[walls[0].direction] ?? walls[0].angle;
  const { ox, oy } = outward(canonAngle);
  const lx = cx + ox * offset;
  const ly = cy + oy * offset;
  const deg = readableDeg((-canonAngle * 180) / Math.PI);

  return (
    <text
      x={lx}
      y={ly}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fontSize * 1.6}
      fontWeight="bold"
      fill="#1a2332"
      transform={`rotate(${deg}, ${lx}, ${ly})`}
    >
      {name}
    </text>
  );
}

// ── Dimension string (EXTERIOR, with extension lines + 45° tick marks) ─────

function DimensionString({ wall, offset, fontSize, thin, scale }: {
  wall: WallSegment2D;
  offset: number;
  fontSize: number;
  thin: number;
  scale: number;
}) {
  const { startX, startY, endX, endY, angle, totalWidth, leftEndJoint, rightEndJoint } = wall;
  if (totalWidth <= 0) return null;

  const { ox, oy } = outward(angle);
  const { dx, dy } = along(angle);
  const dimDist = offset;

  // Dimension measures concrete extent — inset by corner foam at each end
  const lj = leftEndJoint;
  const rj = rightEndJoint;
  const concreteW = totalWidth - lj - rj;
  if (concreteW <= 0) return null;

  // Inset start/end along wall direction by corner joint widths
  const d1x = startX + dx * lj + ox * dimDist;
  const d1y = startY + dy * lj + oy * dimDist;
  const d2x = endX - dx * rj + ox * dimDist;
  const d2y = endY - dy * rj + oy * dimDist;

  // Extension lines from wall face to just past dimension line
  const extOvershoot = scale * 3;
  const e1sx = startX + dx * lj + ox * (scale * 2);
  const e1sy = startY + dy * lj + oy * (scale * 2);
  const e1ex = startX + dx * lj + ox * (dimDist + extOvershoot);
  const e1ey = startY + dy * lj + oy * (dimDist + extOvershoot);
  const e2sx = endX - dx * rj + ox * (scale * 2);
  const e2sy = endY - dy * rj + oy * (scale * 2);
  const e2ex = endX - dx * rj + ox * (dimDist + extOvershoot);
  const e2ey = endY - dy * rj + oy * (dimDist + extOvershoot);

  // 45° tick marks at dimension line ends (AIA standard — diagonal slash)
  const tickLen = scale * 3;
  const tickAngle = angle + Math.PI / 4; // 45° from wall direction
  const tdx = Math.cos(tickAngle) * tickLen;
  const tdy = -Math.sin(tickAngle) * tickLen;

  // Dimension text centered on dimension line
  const midX = (d1x + d2x) / 2;
  const midY = (d1y + d2y) / 2;
  // Offset text outward from the line so it sits above (away from building)
  const textOff = fontSize * 1.2;
  const tx = midX + ox * textOff;
  const ty = midY + oy * textOff;
  const deg = readableDeg((-angle * 180) / Math.PI);

  return (
    <g>
      {/* Extension lines */}
      <line x1={e1sx} y1={e1sy} x2={e1ex} y2={e1ey} stroke="#333" strokeWidth={thin * 0.7} />
      <line x1={e2sx} y1={e2sy} x2={e2ex} y2={e2ey} stroke="#333" strokeWidth={thin * 0.7} />
      {/* Dimension line */}
      <line x1={d1x} y1={d1y} x2={d2x} y2={d2y} stroke="#333" strokeWidth={thin * 0.8} />
      {/* 45° tick marks */}
      <line
        x1={d1x - tdx} y1={d1y - tdy}
        x2={d1x + tdx} y2={d1y + tdy}
        stroke="#333" strokeWidth={thin * 1.2}
      />
      <line
        x1={d2x - tdx} y1={d2y - tdy}
        x2={d2x + tdx} y2={d2y + tdy}
        stroke="#333" strokeWidth={thin * 1.2}
      />
      {/* Dimension text — always reads left-to-right or bottom-to-top */}
      <text
        x={tx}
        y={ty}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize * 1.2}
        fill="#333"
        fontWeight="600"
        transform={`rotate(${deg}, ${tx}, ${ty})`}
      >
        {fmtDim(concreteW)}
      </text>
    </g>
  );
}

// ── Lollipop panel callouts (EXTERIOR, staggered 2 tiers) ──────────────────

function PanelLollipops({ wall, jointWidth: jw, tier1, tier2, radius, thin, fontSize }: {
  wall: WallSegment2D;
  jointWidth: number;
  tier1: number;
  tier2: number;
  radius: number;
  thin: number;
  fontSize: number;
}) {
  const { startX, startY, angle, panels, leftEndJoint } = wall;
  if (panels.length === 0) return null;

  const { dx, dy } = along(angle);
  const { ox, oy } = outward(angle);

  const callouts: JSX.Element[] = [];
  let offset = leftEndJoint;

  for (let i = 0; i < panels.length; i++) {
    const pw = panels[i].mpvar.width;
    // Panel center along wall direction
    const cx = startX + dx * (offset + pw / 2);
    const cy = startY + dy * (offset + pw / 2);

    // Stagger: even → tier 1 (short), odd → tier 2 (long)
    const stemLen = i % 2 === 0 ? tier1 : tier2;

    // Stem base at exterior wall face
    const bx = cx;
    const by = cy;

    // Lollipop circle center
    const lx = cx + ox * stemLen;
    const ly = cy + oy * stemLen;

    const label = panels[i].mpvar.panelMark || `P${i + 1}`;
    const charCount = label.length;
    const textSize = Math.min(fontSize * 0.85, (radius * 1.5) / Math.max(charCount * 0.55, 1));
    const rectW = Math.max(charCount * textSize * 0.65 + textSize * 0.6, radius * 1.3);
    const rectH = textSize * 1.6;
    const cornerRad = textSize * 0.25;
    const deg = readableDeg((-angle * 180) / Math.PI);

    // Stem ends at rectangle near edge
    const stemEndX = lx - ox * (rectH / 2);
    const stemEndY = ly - oy * (rectH / 2);

    callouts.push(
      <g key={panels[i].id}>
        {/* Leader stem */}
        <line
          x1={bx} y1={by}
          x2={stemEndX} y2={stemEndY}
          stroke="#333" strokeWidth={thin * 0.8}
        />
        {/* Callout rectangle */}
        <rect
          x={lx - rectW / 2} y={ly - rectH / 2}
          width={rectW} height={rectH}
          rx={cornerRad} ry={cornerRad}
          fill="white" stroke="#333" strokeWidth={thin * 0.8}
          transform={`rotate(${deg}, ${lx}, ${ly})`}
        />
        {/* Panel mark text */}
        <text
          x={lx} y={ly}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={textSize}
          fill="#333"
          fontWeight="600"
          transform={`rotate(${deg}, ${lx}, ${ly})`}
        >
          {label}
        </text>
      </g>,
    );

    offset += pw;
    if (i < panels.length - 1) offset += jw;
  }
  return <g>{callouts}</g>;
}

// ── Grid axis labels (numbered X, lettered Y, circle bubbles) ───────────

/** Convert 0-based index to spreadsheet-style letter: 0→A, 25→Z, 26→AA */
function gridLetter(n: number): string {
  let label = '';
  let i = n + 1;
  while (i > 0) {
    i--;
    label = String.fromCharCode(65 + (i % 26)) + label;
    i = Math.floor(i / 26);
  }
  return label;
}

function GridAxisLabels({ vbX, vbY, vbW, vbH, minX, minY, scale, thin }: {
  vbX: number; vbY: number; vbW: number; vbH: number;
  minX: number; minY: number;
  scale: number; thin: number;
}) {
  const sectionSize = 600;
  const bubbleR = scale * 8;
  const labelFont = scale * 10;
  const color = '#5080b0';
  const textColor = '#fff';

  // Grid origin is (0,0) — same as the 3D view and building footprint origin.
  // Label number = coord/sectionSize + 1, so gridline at 0 = "1", 600 = "2", etc.
  const firstX = Math.floor(vbX / sectionSize) * sectionSize;
  const firstY = Math.floor(vbY / sectionSize) * sectionSize;

  const labels: JSX.Element[] = [];

  // X-axis labels (numbers) — across the top, above the building
  for (let x = firstX; x <= vbX + vbW; x += sectionSize) {
    const num = Math.round(x / sectionSize) + 1;
    if (num < 1) continue; // skip negative grid coords
    const labelY = minY - bubbleR * 3.5;
    labels.push(
      <g key={`gx-${num}`}>
        <circle cx={x} cy={labelY} r={bubbleR} fill={color} stroke={color} strokeWidth={thin * 0.5} />
        <text
          x={x} y={labelY}
          textAnchor="middle" dominantBaseline="central"
          fontSize={labelFont} fill={textColor} fontWeight="bold"
        >
          {String(num)}
        </text>
      </g>,
    );
  }

  // Y-axis labels (letters) — along the left side, beside the building
  for (let y = firstY; y <= vbY + vbH; y += sectionSize) {
    const idx = Math.round(y / sectionSize);
    if (idx < 0) continue;
    const labelX = minX - bubbleR * 3.5;
    labels.push(
      <g key={`gy-${idx}`}>
        <circle cx={labelX} cy={y} r={bubbleR} fill={color} stroke={color} strokeWidth={thin * 0.5} />
        <text
          x={labelX} y={y}
          textAnchor="middle" dominantBaseline="central"
          fontSize={labelFont} fill={textColor} fontWeight="bold"
        >
          {gridLetter(idx)}
        </text>
      </g>,
    );
  }

  return <g>{labels}</g>;
}
